/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react'
import { Modal, Button, Spinner } from 'react-bootstrap'
import { Download, TriangleAlert } from 'lucide-react'
import api from '../../../config/AxiosInstance'

const FilePreviewModal = ({ show, onHide, fileUrl, fileName, fileType }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [previewBlobUrl, setPreviewBlobUrl] = useState('')

  const getFileExtension = (url) => {
    if (!url) return ''
    if (url.startsWith('blob:')) return ''
    const path = url.split('?')[0]
    return path.split('.').pop().toLowerCase()
  }

  const isImage = (url) => {
    if (fileType && fileType.startsWith('image/')) return true
    const ext = getFileExtension(url)
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)
  }

  const isPdf = (url) => {
    if (fileType === 'application/pdf' || fileType === 'pdf') return true
    const ext = getFileExtension(url)
    return ext === 'pdf'
  }

  useEffect(() => {
    let objectUrl = ''

    const preparePreview = async () => {
      if (!show || !fileUrl) return

      setLoading(true)
      setError(false)

      if (isPdf(fileUrl) && !fileUrl.startsWith('blob:')) {
        try {
          const response = await fetch(fileUrl)
          if (!response.ok) throw new Error('Failed to fetch file')
          const blob = await response.blob()
          objectUrl = URL.createObjectURL(
            new Blob([blob], { type: 'application/pdf' })
          )
          setPreviewBlobUrl(objectUrl)
        } catch (err) {
          console.error('Error fetching PDF preview:', err)
          if (fileUrl.startsWith('http')) {
            setPreviewBlobUrl(
              `https://docs.google.com/viewer?url=${encodeURIComponent(
                fileUrl
              )}&embedded=true`
            )
          } else {
            setError(true)
          }
        } finally {
          setLoading(false)
        }
      } else {
        setPreviewBlobUrl(fileUrl)
        if (!isImage(fileUrl) && !isPdf(fileUrl)) {
          setLoading(false)
        }
      }
    }

    preparePreview()

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [show, fileUrl])

  const handleDownload = () => {
    if (!fileUrl) return

    const apiBaseUrl = api.defaults.baseURL || ''
    const downloadUrl = `${apiBaseUrl}/upload/download?url=${encodeURIComponent(
      fileUrl
    )}`

    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = fileName || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const renderContent = () => {
    if (!fileUrl) return null

    if (error) {
      return (
        <div className="text-center p-5 text-danger">
          <TriangleAlert size={48} className="mb-3" />
          <h5>Failed to load file</h5>
          <p>The file might be missing or the format is not supported.</p>
        </div>
      )
    }

    return (
      <div className="position-relative" style={{ minHeight: '300px' }}>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle text-center"
            style={{ zIndex: 1 }}
          >
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading preview...</p>
          </div>
        )}

        {isImage(fileUrl) ? (
          <img
            src={previewBlobUrl || fileUrl}
            alt={fileName}
            className="img-fluid d-block mx-auto rounded shadow-sm"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false)
              setError(true)
            }}
            style={{ maxHeight: '70vh', objectFit: 'contain' }}
          />
        ) : isPdf(fileUrl) ? (
          <iframe
            src={
              previewBlobUrl
                ? previewBlobUrl.includes('google.com/viewer')
                  ? previewBlobUrl
                  : `${previewBlobUrl}#toolbar=0`
                : ''
            }
            title={fileName}
            width="100%"
            height="600px"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false)
              setError(true)
            }}
            className="rounded border-0 shadow-sm"
          />
        ) : (
          <div className="text-center p-5 text-warning">
            <TriangleAlert size={48} className="mb-3" />
            <h5>Preview not available</h5>
            <p>
              This file format cannot be previewed. Please download to view.
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="file-preview-modal"
    >
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="fs-5 text-truncate pe-4" title={fileName}>
          {fileName || 'File Preview'}
        </Modal.Title>
        <button
          type="button"
          className="btn-close position-absolute end-0 top-0 m-3"
          onClick={onHide}
          aria-label="Close"
        />
      </Modal.Header>
      <Modal.Body className="p-4">{renderContent()}</Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="light" onClick={onHide} className="px-4">
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleDownload}
          className="px-4 d-flex align-items-center"
        >
          <Download size={16} className="me-2" />
          Download
        </Button>
      </Modal.Footer>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .file-preview-modal .modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        .file-preview-modal iframe {
          background-color: #f8f9fa;
        }
      `
        }}
      />
    </Modal>
  )
}

export default FilePreviewModal
